<template>
    <b-modal
        :ref="modalRef"
        title-tag="h2"
        title="Email Lab"
        @hidden="close"
        lazy
        no-close-on-backdrop
        no-close-on-esc
        no-enforce-focus
    >
        <b-form-group label-for="to" label="To:" label-cols="3">
            <recipient-email
                v-model="selectedReceivers"
                id="selectedReceivers"
                multiple
                placeholder="Client or Contact Email (required)"
                @input="handleRecipientChange"
                :key="recipientComponentKey"
                :client="clientDetails"
            />
        </b-form-group>

        <b-form-group label-for="subject" label="Subject:" label-cols="3">
            <b-form-input
                id="subject"
                v-model="subject"
                placeholder="Subject (optional)"
                name="subject"
            ></b-form-input>
        </b-form-group>

        <b-form-group label-for="message" label="Message:" label-cols="3">
            <tinymce
                id="message"
                :init="tinymceConfig"
                v-model="message"
                @onKeyPress="contentChanged"
            />
        </b-form-group>

        <b-form-group label-for="attachment" label="Include:" label-cols="3">
            <b-form-checkbox
                id="attachment"
                v-model="attachment"
                name="attachment"
                plain
                class="col-form-label"
            >
                <span class="fw-normal">Attachment</span>
            </b-form-checkbox>
        </b-form-group>

        <template v-slot:modal-footer>
            <modal-footer
                :is-loading="isLoading"
                ok-title="Send"
                @ok-action="send"
                @cancel-action="close"
            />
        </template>
    </b-modal>
</template>
<script>
import axios from "axios";
import PatientLabService from "@/services/patient/patient-lab";
import Email from "@/services/email";
import CEMAIL from "@/constants/email";
import tinymce from "@tinymce/tinymce-vue";
import tinymceHelper from "@/module/commons/helper-tinymce"; // helper
import RecipientEmail from "@/components/commons/recipients-email";

export default {
    components: {
        tinymce,
        RecipientEmail,
        modalFooter: () => import("@/components/elements/modal-footer")
    },
    props: {
        modalRef: {
            type: String,
            required: true
        },
        patientDetails: {
            type: Object,
            default: null
        },
        labId: {
            type: String,
            default: null
        }
    },
    data() {
        return {
            clientDetails: null,
            selectedReceivers: [],
            subject: null,
            message: null,
            defaultMessage: null,
            recipientList: [],
            receiverNames: "",
            isLoading: false,
            attachment: false,
            tinymceConfig: {
                ...tinymceHelper.core, // use spread operator to inject all vetter-related configs
                toolbar: false,
                plugins: tinymceHelper.plugins
            },
            recipientComponentKey: 0,
            formError: [],
            isOpen: false,
            isContentChanged: false
        };
    },
    computed: {
        isOkDisabled() {
            return (
                this.message == null ||
                this.message == "" ||
                this.selectedReceivers.length < 1
            );
        },
        owner() {
            const owners = this.patientDetails.primary_owner;
            return owners[0] || this._setting.clinic_default_owner || null;
        }
    },
    methods: {
        async open() {
            this.clientDetails = this.owner;
            await this.getEmailTemplate();
            this.$nextTick(() => {
                this.$refs[this.modalRef].show();
            });
        },
        contentChanged() {
            if (!this.isContentChanged) {
                this.isContentChanged = true;
            }
            return true;
        },
        async getEmailTemplate() {
            const patientIds = this.patientDetails
                ? [this.patientDetails.id]
                : null;
            let params = {
                business_id: this._setting.business.id,
                user_id: this.$root.currentActor.id,
                email_type: CEMAIL.TYPE.LAB,
                client_id: this.clientDetails ? this.clientDetails.id : "",
                patient_id: patientIds,
                timezone: this._timeZone,
                fmt: this.getDateFormat()
            };

            let response = await Email.renderEmailMessage(params);
            let defaults = response?.resources?.message;

            if (defaults) {
                this.subject = defaults.subject;
                this.message = defaults.message;
                this.defaultMessage = defaults.message;
                setTimeout(function () {
                    this.setDefaultRecipient();
                }, 300);
            }
        },
        async send() {
            let self = this;
            self.isLoading = true;
            const params = {
                labId: self.labId,
                includeAttachment: self.attachment,
                to: self.selectedReceivers.map(r => r.to_id),
                subject: self.subject,
                message: self.message
            };
            await PatientLabService.sendEmailLab(params)
                .then(result => {
                    self.isLoading = false;
                    if (result.code == 200) {
                        self._message("Lab report emailed");
                        self.close();
                    } else {
                        self._message("Error: Unable to send email", "danger");
                    }
                })
                .catch(err => {
                    console.log(err);
                    self.isLoading = false;
                    self._message("Error: Unable to send email", "danger");
                });
        },
        close() {
            this.isLoading = false;
            this.$refs[this.modalRef].hide();
        },
        async handleRecipientChange(values = []) {
            this.isLoading = true;

            if (values.length == 0 && !this.isContentChanged) {
                this.message = this.defaultMessage;
            } else {
                let recipients = [];
                for (const rec of values) {
                    recipients.push(rec.to_id);
                }

                let formData = new FormData();
                let msg = this.message || "";

                let header = msg.slice(0, msg.indexOf("</p>"));
                let body = msg.substring(msg.length, header.length);

                formData.append("recipients", recipients.join(","));
                formData.append("email_type", this.EMAIL_TYPE.LAB);
                formData.append("message_body", body);
                formData.append("header", header);

                const res = await axios.post(
                    `${process.env.CI_APPS}business/email_setting/change_recipient`,
                    formData
                );

                if (res.data && res.data.message) {
                    this.message = res.data.message || this.defaultMessage;
                }
            }
            this.isLoading = false;
        },
        setDefaultRecipient() {
            const { primary_owner: owners } = this.patientDetails;

            for (const owner of owners) {
                if (owner && owner.email && owner.email !== "") {
                    this.clientDetails = owner;
                    this.recipientComponentKey++; // force re-render
                    break;
                }
            }
        }
    }
};
</script>
